import React, { useEffect, forwardRef, useImperativeHandle } from "react";
import { Center } from "@/common/css";
import SingleSelect from "@/common/custom/SingleSelect";
import { MultiSelectFromAPI } from "@/common/custom/MultiSelectFromAPI";
import { MultiSelect } from "@/common/custom/MultiSelect";
import Button from "@/common/ui/Button";
import { LightText2 } from "@/common/ui/Text";
import { filterEmptyIcon, modalCloseIcon } from "@/dmr/common/SvgIcons";
import CustomDatePicker from "@/common/input/CustomDatePicker";
import CustomDateRangePicker from "@/common/input/CustomDateRangePicker";
import DurationInput from "@/common/input/DurationInput";
import { EMPTY_VALUE_FILTER_OPERATOR } from "@/utils/constant/global.constant";

// Payment filter operators
const operators: any = {
  // 1. Received on - Date picker (range)
  received_on: [
    { label: "Between (from and to date)", value: "between", isDefault: true, fieldType: "date-range" },
    { label: "On", value: "on", fieldType: "date" },
    { label: "Before", value: "before", fieldType: "date" },
    { label: "After", value: "after", fieldType: "date" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "last month", value: "last_month" },
    { label: "last week", value: "last_week" },
    { label: "last year", value: "last_year" },
    { label: "this week", value: "this_week" },
    { label: "this month", value: "this_month" },
    { label: "this year", value: "this_year" },
    { label: "In the last", value: "in_the_last" },
    { label: "Due in", value: "due_in" },
    { label: "is empty", value: "is_empty" },
    { label: "is not empty", value: "is_not_empty" },
  ],
  // 2. Status - Dropdown (multi-select) - Values: Received, Waiting, Unopened
  status: [
    { label: "Is", value: "equals", fieldType: "multi-select" },
    { label: "Isn't", value: "not_equals", fieldType: "multi-select" },
    { label: "Contains", value: "contains", isDefault: true, fieldType: "multi-select" },
    { label: "Doesn't contain", value: "not_contains", fieldType: "multi-select" },
    { label: "is empty", value: "is_empty" },
    { label: "is not empty", value: "is_not_empty" },
  ],
  // 3. Employee Name - Lookup - Users
  employee_name: [
    { label: "Contains", value: "contains", isDefault: true, fieldType: "multi-select" },
    { label: "Doesn't contain", value: "not_contains", fieldType: "multi-select" },
    { label: "Is", value: "equals", fieldType: "multi-select" },
    { label: "Isn't", value: "not_equals", fieldType: "multi-select" },
    { label: "starts with", value: "startsWith", fieldType: "multi-select" },
    { label: "ends with", value: "endsWith", fieldType: "multi-select" },
    { label: "is empty", value: "is_empty" },
    { label: "is not empty", value: "is_not_empty" },
  ],
  // 4. Paid By - Text
  paid_by: [
    { label: "Contains", value: "contains", isDefault: true, fieldType: "text" },
    { label: "Doesn't contain", value: "not_contains", fieldType: "text" },
    { label: "Is", value: "equals", fieldType: "text" },
    { label: "Isn't", value: "not_equals", fieldType: "text" },
    { label: "starts with", value: "startsWith", fieldType: "text" },
    { label: "ends with", value: "endsWith", fieldType: "text" },
    { label: "is empty", value: "is_empty" },
    { label: "is not empty", value: "is_not_empty" },
  ],
  // 5. Transaction ID - Text
  transaction_id: [
    { label: "Contains", value: "contains", isDefault: true, fieldType: "text" },
    { label: "Doesn't contain", value: "not_contains", fieldType: "text" },
    { label: "Is", value: "equals", fieldType: "text" },
    { label: "Isn't", value: "not_equals", fieldType: "text" },
    { label: "starts with", value: "startsWith", fieldType: "text" },
    { label: "ends with", value: "endsWith", fieldType: "text" },
    { label: "is empty", value: "is_empty" },
    { label: "is not empty", value: "is_not_empty" },
  ],
  // 6. Proof ID - Text
  proof_id: [
    { label: "Contains", value: "contains", isDefault: true, fieldType: "text" },
    { label: "Doesn't contain", value: "not_contains", fieldType: "text" },
    { label: "Is", value: "equals", fieldType: "text" },
    { label: "Isn't", value: "not_equals", fieldType: "text" },
    { label: "starts with", value: "startsWith", fieldType: "text" },
    { label: "ends with", value: "endsWith", fieldType: "text" },
    { label: "is empty", value: "is_empty" },
    { label: "is not empty", value: "is_not_empty" },
  ],
  // 7. Payment Method - Dropdown (multi-select)
  payment_method: [
    { label: "Is", value: "equals", isDefault: true, fieldType: "multi-select" },
    { label: "Isn't", value: "not_equals", fieldType: "multi-select" },
    { label: "Contains", value: "contains", fieldType: "multi-select" },
    { label: "Doesn't contain", value: "not_contains", fieldType: "multi-select" },
    { label: "is empty", value: "is_empty" },
    { label: "is not empty", value: "is_not_empty" },
  ],
};

const FIELD_TYPE: any = {
  // Multi-select operators
  contains: "multi-select",
  not_contains: "multi-select",
  equals: "multi-select",
  not_equals: "multi-select",
  startsWith: "multi-select",
  endsWith: "multi-select",

  // Date operators
  on: "date",
  before: "date",
  after: "date",
  between: "date-range",

  // No input needed operators (empty values)
  is_empty: null,
  is_not_empty: null,
  today: null,
  yesterday: null,
  last_week: null,
  last_month: null,
  last_year: null,
  this_week: null,
  this_month: null,
  this_year: null,

  // Duration operators
  in_the_last: "duration",
  due_in: "duration",

  // Number/Currency operators
  greater_than: "number",
  less_than: "number",
};

const OPTIONS_API: any = {
  // Text fields - no API needed for text input
  transaction_id: "/payment/filter-option?option_key=transaction_id",
  proof_id: "/payment/filter-option?option_key=proof_id",
  paid_by: "/payment/filter-option?option_key=paid_by",
  
  // Lookup fields
  employee_name: "/employee/option?option_key=payment_employees",
  
  // Dropdown fields - payment_method uses static options
  payment_method: null,
  
  // Date fields - no API needed
  received_on: null,
};

// Static options for Status field
const STATUS_OPTIONS = [
  { label: "Received", value: "Received" },
  { label: "Waiting", value: "Waiting" },
  { label: "Expired", value: "Expired" },
];

// Static options for Payment Method field
const PAYMENT_METHOD_OPTIONS = [
  { label: "Bank Transfer", value: "bank_transfer" },
  { label: "UPI", value: "upi" },
  { label: "Wallet", value: "wallet" },
  { label: "PayPal", value: "paypal" },
  { label: "Stripe", value: "stripe" },
  { label: "Razorpay", value: "razorpay" },
  { label: "Card", value: "card" },
];

// Payment table fields for filter
const PAYMENT_TABLE_FIELDS = [
  { label: "Received on", accessorKey: "received_on", hideOnFilter: false },
  { label: "Status", accessorKey: "status", hideOnFilter: false },
  { label: "Employee name", accessorKey: "employee_name", hideOnFilter: false },
  { label: "Paid by", accessorKey: "paid_by", hideOnFilter: false },
  { label: "Transaction ID", accessorKey: "transaction_id", hideOnFilter: false },
  { label: "Proof ID", accessorKey: "proof_id", hideOnFilter: false },
  { label: "Payment Method", accessorKey: "payment_method", hideOnFilter: true },
];

interface PaymentFilterProps {
  onClose?: (props?: any) => void;
  preventClickOutside?: boolean;
  onApplyFilters?: (filters: any[]) => void;
  appliedFilters?: any[];
  onResetFilters?: () => void;
  onAddFilter?: () => void;
}

const PaymentFilter = forwardRef<any, PaymentFilterProps>(({ onApplyFilters, appliedFilters = [], onResetFilters, onAddFilter }, ref) => {
  // Initialize filters with applied filters if provided
  const [filters, setFilters] = React.useState<any[]>(appliedFilters);
  const filterContainerRef = React.useRef<HTMLDivElement>(null);
  
  // Helper function to compare filters for equality
  const areFiltersEqual = (filters1: any[], filters2: any[]): boolean => {
    if (filters1.length !== filters2.length) return false;
    
    return filters1.every((filter1, index) => {
      const filter2 = filters2[index];
      if (!filter2) return false;
      
      // Compare field
      if (filter1.field?.value !== filter2.field?.value) return false;
      
      // Compare operator
      const op1 = typeof filter1.operator === 'object' ? filter1.operator?.value : filter1.operator;
      const op2 = typeof filter2.operator === 'object' ? filter2.operator?.value : filter2.operator;
      if (op1 !== op2) return false;
      
      // Compare values
      if (JSON.stringify(filter1.value) !== JSON.stringify(filter2.value)) return false;
      
      return true;
    });
  };

  const handleFilterChange = (index: number, key: any, value: any) => {
    const newFilters = [...filters];
    
    // If field is being set, automatically set the default operator
    if (key === 'field' && value?.value) {
      const defaultOperator = operators[value.value]?.find((op: any) => op.isDefault);
      newFilters[index] = {
        ...newFilters[index],
        field: value,
        operator: defaultOperator ? {
          label: defaultOperator.label,
          value: defaultOperator.value
        } : null,
        value: []
      };
    } else {
      newFilters[index] = { ...newFilters[index], [key]: value };
    }
    
    setFilters(newFilters);
  };


  const filterCanBeSet = filters.every((f: any) => {
    // If no field is selected, filter is not complete
    if (!f.field?.value) {
      return false;
    }
    
    // If no operator is selected, try to use the default operator
    let operator = f.operator;
    if (!operator && f.field?.value) {
      const defaultOperator = operators[f.field.value]?.find((op: any) => op.isDefault);
      if (defaultOperator) {
        operator = { label: defaultOperator.label, value: defaultOperator.value };
      } else {
        return false;
      }
    }
    
    const operatorInfo = operators[f.field?.value]?.find((op: any) => op.value === operator?.value);
    const fieldType = operatorInfo?.fieldType || FIELD_TYPE[operator?.value];

    if (fieldType === null) {
      return true;
    }
    if (!Array.isArray(f.value) || f.value.length === 0) {
      return false;
    }

    if (fieldType === "duration") {
      return typeof f.value[0] === "object" && f.value[0] && f.value[0].amount && f.value[0].unit;
    } else if (fieldType === "date-range" || operator?.value === "between") {
      return (
        f.value.length === 2 &&
        f.value[0] !== null &&
        f.value[0] !== undefined &&
        f.value[0] !== "" &&
        f.value[1] !== null &&
        f.value[1] !== undefined &&
        f.value[1] !== ""
      );
    } else if (fieldType === "text" || fieldType === "number") {
      // For text and number fields, check if the value is not empty
      return f.value[0] !== null && f.value[0] !== undefined && f.value[0] !== "";
    } else if (fieldType === "single-select") {
      // For single select, check if a value is selected
      return f.value[0] !== null && f.value[0] !== undefined && f.value[0] !== "";
    } else if (fieldType === "multi-select") {
      // For multi select, check if at least one value is selected
      return f.value.length > 0 && f.value.every((v: any) => v !== null && v !== undefined && v !== "");
    } else {
      return f.value[0] !== null && f.value[0] !== undefined && f.value[0] !== "";
    }
  });

  const handleApplyFilter = () => {
    // Format filters for backend - convert operator objects to simple values
    const formattedFilters = filters.map((filter: any) => {
      // Use default operator if none is set
      let operator = filter.operator;
      if (!operator && filter.field?.value) {
        const defaultOperator = operators[filter.field.value]?.find((op: any) => op.isDefault);
        if (defaultOperator) {
          operator = { label: defaultOperator.label, value: defaultOperator.value };
        }
      }
      
      return {
        field: {
          label: filter.field?.label || "",
          value: filter.field?.value || ""
        },
        operator: {
          label: operator?.label || "",
          value: operator?.value || ""
        },
        value: filter.value || []
      };
    });
    
    // Check if the new filters are the same as the currently applied filters
    if (areFiltersEqual(formattedFilters, appliedFilters)) {
      // Same filters are being applied again, don't make API call
      return;
    }
        
    // Send formatted filters to parent component
    if (onApplyFilters) {
      onApplyFilters(formattedFilters);
    }
    
    // Don't close the filter panel after applying - let user manually close it
  };

  const handleRemoveFilter = (index: number) => {
    const newFilters = filters.filter((_, i) => i !== index);
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    // Check if there are no filters to reset
    if (appliedFilters.length === 0) {
      // No filters to reset, don't make API call
      setFilters([]);
      return;
    }
    
    setFilters([]);
    // Call the parent's reset function to clear filters and refresh data
    if (onResetFilters) {
      onResetFilters();
    }
  };

  const handleAddFilter = () => {
    // Check if there's already an empty filter (no field selected)
    const hasEmptyFilter = filters.some((filter: any) => !filter.field?.value);
    
    // Don't add a new filter if there's already an empty one
    if (hasEmptyFilter) {
      return;
    }
    
    setFilters([{ field: null, operator: null, value: [] }, ...filters]);
    // Call the parent's onAddFilter if provided
    if (onAddFilter) {
      onAddFilter();
    }
  };

  // Expose the addFilter function to parent components
  useImperativeHandle(ref, () => ({
    addFilter: handleAddFilter
  }));

  // Update filters when appliedFilters prop changes
  useEffect(() => {
    if (appliedFilters && appliedFilters.length > 0) {
      // Transform appliedFilters to match the expected format
      const transformedFilters = appliedFilters.map((filter: any) => {
        const transformedFilter = { ...filter };
        
        // If operator is a string, convert it to object format
        if (filter.operator && typeof filter.operator === 'string') {
          const operatorInfo = operators[filter.field?.value]?.find((op: any) => op.value === filter.operator);
          transformedFilter.operator = {
            label: operatorInfo?.label || filter.operator,
            value: filter.operator,
          };
        }
        
        // Normalize values for status and payment_method fields to ensure they are strings
        if (filter.field?.value === 'status' || filter.field?.value === 'payment_method') {
          if (filter.value && Array.isArray(filter.value)) {
            transformedFilter.value = filter.value.map((v: any) => {
              // If it's an object with a value property, extract the value
              if (typeof v === 'object' && v.value) {
                return v.value;
              }
              // If it's already a string, keep it as is
              return v;
            });
          }
        }
        
        return transformedFilter;
      });
      
      setFilters(transformedFilters);
    } else {
      setFilters([]);
    }
  }, [appliedFilters]);


  return (
    <div 
      ref={filterContainerRef}
      className="p-3 sm:p-4 bg-white flex flex-col justify-between w-full h-full !text-text-default"
    >
      <div className="w-full flex-1 overflow-y-auto">
        {filters.length > 0 ? (
          <>
            {filters.map((filter: any, index: number) => {
              const operatorInfo = operators[filter.field?.value]?.find((op: any) => op.value === filter.operator?.value);
              const fieldType = operatorInfo?.fieldType || FIELD_TYPE[filter.operator?.value];
              
              return filter?.field?.value ? (
                <div className="flex justify-between items-start w-full mb-3" key={filter.field?.value + "-" + index}>
                  <div key={index} className="flex flex-col w-full min-w-[calc(100%-40px)] max-w-[calc(100%-40px)]">
                    <div className="flex gap-x-2 items-center w-full">
                      <LightText2 text={filter?.field?.label} className="font-medium whitespace-nowrap" />
                      <SingleSelect
                        key={`operator-${index}-${filter.field?.value}`}
                        options={(() => {
                          const options = operators[filter.field?.value]?.map((Operator: any) => ({ label: Operator.label, value: Operator.value })) || [];
                          return options;
                        })()}
                        placeholder="Select operator"
                        containerClass="w-fit flex item-center h-9"
                        textClassname="text-13"
                        onChange={(value) => {
                          handleFilterChange(index, "operator", value);
                        }}
                        selectedValue={filter.operator ?? operators[filter.field?.value]?.find((op: any) => op.isDefault)?.value ?? ""}
                        defaultValue={(() => {
                          const defaultOp = operators[filter.field?.value]?.find((f: any) => f?.isDefault);
                          return defaultOp ? { label: defaultOp.label, value: defaultOp.value } : undefined;
                        })()}
                        className="w-full text-13 leading-normal font-normal justify-between text-text-primary"
                        dropdownClassName="w-fit top-9 z-[60]"
                        listClassName="max-h-[200px] overflow-y-auto text-text-default bg-white"
                        activeClass="text-text-primary hover:bg-primaryOpacity-10"
                        showSearch={false}
                      />
                    </div>
                    {(() => {
                      const operatorValue = filter?.operator?.value;

                      if (EMPTY_VALUE_FILTER_OPERATOR.includes(operatorValue)) {
                        return null;
                      }

                      // Use fieldType from operator if available, otherwise fall back to FIELD_TYPE mapping
                      const actualFieldType = operatorInfo?.fieldType || fieldType;

                      if (actualFieldType === "duration") {
                        return (
                          <DurationInput
                            value={
                              filter.value?.[0]
                                ? {
                                    amount: filter.value[0].amount || 0,
                                    unit: filter.value[0].unit || "days",
                                  }
                                : undefined
                            }
                            onChange={(value) => handleFilterChange(index, "value", [value])}
                            type={operatorValue === "in_the_last" ? "in_the_last" : "due_in"}
                          />
                        );
                      }

                      if (actualFieldType === "date-range") {
                        return (
                          <CustomDateRangePicker
                            value={
                              filter.value?.[0]
                                ? {
                                    startDate: filter.value[0],
                                    endDate: filter.value[1] || "",
                                  }
                                : undefined
                            }
                            onChange={(value) => handleFilterChange(index, "value", [value.startDate, value.endDate])}
                            placeholder="Select date"
                            maxToday={false}
                          />
                        );
                      }

                      if (actualFieldType === "date") {
                        return (
                          <CustomDatePicker
                            value={filter.value?.[0] ?? ""}
                            onChange={(value) => handleFilterChange(index, "value", [value])}
                            placeholder="Select date"
                            maxToday={false}
                          />
                        );
                      }

                      if (actualFieldType === "number") {
                        return (
                          <input
                            type="number"
                            className="border rounded px-2 py-1 text-sm w-full"
                            onChange={(e) => handleFilterChange(index, "value", [e.target.value])}
                            value={filter.value?.[0] ?? ""}
                            placeholder="Enter amount"
                          />
                        );
                      }

                      // Text input for text fields
                      if (actualFieldType === "text") {
                        const getPlaceholderText = (fieldValue: string) => {
                          switch (fieldValue) {
                            case "proof_id":
                              return "Enter Proof ID";
                            case "transaction_id":
                              return "Enter Transaction ID";
                            case "paid_by":
                              return "Enter Paid by";
                            default:
                              return "Enter text";
                          }
                        };

                        return (
                          <input
                            type="text"
                            className="border rounded px-2 py-1 text-sm w-full"
                            onChange={(e) => handleFilterChange(index, "value", [e.target.value])}
                            value={filter.value?.[0] ?? ""}
                            placeholder={getPlaceholderText(filter.field?.value)}
                          />
                        );
                      }

                      // Single select for dropdown fields
                      if (actualFieldType === "single-select") {
                        return (
                          <SingleSelect
                            options={[]} // Will be populated from API
                            placeholder="Select"
                            containerClass="w-full flex item-center h-9"
                            textClassname="text-13"
                            onChange={(value) => handleFilterChange(index, "value", [value])}
                            selectedValue={filter.value?.[0] ?? ""}
                            className="w-full text-13 leading-normal font-normal border-[1px] justify-between"
                            dropdownClassName="w-full top-9"
                            listClassName="max-h-[200px] overflow-y-auto"
                            showSearch
                          />
                        );
                      }

                      if (actualFieldType === "multi-select") {
                        // Use static options for Status and Payment Method fields
                        if (filter.field?.value === "status" || filter.field?.value === "payment_method") {
                          const options = filter.field?.value === "status" ? STATUS_OPTIONS : PAYMENT_METHOD_OPTIONS;
                          const placeholderText = filter.field?.value === "status" ? "Select status options" : "Select payment method options";
                          
                          return (
                            <MultiSelect
                              options={options}
                              onValueChange={(value) => handleFilterChange(index, "value", value)}
                              defaultValue={filter.value ?? []}
                              placeholder={placeholderText}
                              maxCount={3}
                              key={filter.field?.value + "-multi-select"}
                              modalPopover={false}
                              showSearch={false}
                            />
                          );
                        }
                        
                        // Use API for other multi-select fields
                        return (
                          <MultiSelectFromAPI
                            optionsApiUrl={OPTIONS_API[filter.field?.value]}
                            onValueChange={(value) => handleFilterChange(index, "value", value)}
                            defaultValue={filter.value ?? []}
                            placeholder="Select"
                            showSearch={filter.field?.value === "employee_name"}
                            maxCount={3}
                            key={filter.field?.value + "-multi-select"}
                            modalPopover={false}
                            labelFormatter={filter.field?.value === "employee_name" ? (option: any) => {
                              // Format employee name to show both ID and name
                              // API returns employee data with empId and label fields
                              if (option.empId && option.label) {
                                return `${option.empId} - ${option.label}`;
                              }
                              // Fallback to original label if format is different
                              return option.label;
                            } : undefined}
                          />
                        );
                      }

                      return null;
                    })()}
                  </div>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFilter(index);
                    }} 
                    className="text-rose-500 text-10 hover:text-rose-700 pt-2"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <div key={"empty-" + index} className="flex mb-4 flex-col mt-2">
                  <Center className="w-full justify-between pb-2">
                    <span className="text-13 !leading-normal font-medium">Select filter field</span>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFilter(index);
                      }} 
                      className="text-gray-400 hover:text-gray-500" 
                      aria-label="Close"
                    >
                      {modalCloseIcon}
                    </button>
                  </Center>
                  <SingleSelect
                    isDarkDropdown={false}
                    options={PAYMENT_TABLE_FIELDS.filter(
                      (field) => !field?.hideOnFilter && !filters.some((f: any) => f.field?.value === field.accessorKey)
                    ).map((field) => ({
                      label: field.label,
                      value: field.accessorKey,
                    }))}
                    placeholder="Select"
                    containerClass="w-full flex item-center z-50 h-9"
                    textClassname="text-13"
                    onChange={(value) => handleFilterChange(index, "field", value)}
                    selectedValue={filter.field?.value ?? ""}
                    className="w-full text-13 leading-normal font-normal border-[1px] justify-between"
                    dropdownClassName="w-full top-9"
                    listClassName="max-h-[200px] overflow-y-auto"
                    openOnRender
                    showSearch
                  />
                </div>
              );
            })}
          </>
        ) : (
          <div className="!h-full flex w-full flex-col items-center justify-center py-8">
            <div className="flex items-center flex-col gap-3 sm:gap-4 px-3 sm:px-4 text-center">
              {filterEmptyIcon}
              <LightText2 text="Add filters to narrow the payments you want to see!" />
            </div>
          </div>
        )}
      </div>

      <div className="flex mt-3 sm:mt-4 justify-between flex-shrink-0 border-t border-gray-100 pt-3">
        <Button 
          onClick={(e) => {
            e.stopPropagation();
            handleResetFilters();
          }} 
          variant="outline" 
          size="small" 
        >
          Reset
        </Button>
        <Button 
          onClick={(e) => {
            e.stopPropagation();
            handleApplyFilter();
          }} 
          variant="primary" 
          size="small" 
          disabled={!filters.length || !filterCanBeSet}
        >
          Apply
        </Button>
      </div>
    </div>
  );
});

PaymentFilter.displayName = 'PaymentFilter';

export default PaymentFilter;
